/**
 * Protected Route Component
 * Validates session token and authentication status before rendering children
 */

import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAppSelector } from "@/store/hooks";
import { getSessionToken, setRedirectUrl } from "@/utils/storage";
import { logger } from "@/utils/logger";

interface ProtectedRouteProps {
  children?: React.ReactNode;
  requireAdmin?: boolean;
}

export function ProtectedRoute({
  children,
  requireAdmin,
}: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const sessionToken = getSessionToken();

  // Debug logging
  useEffect(() => {
    logger.log("ProtectedRoute check:", {
      isAuthenticated,
      hasUser: !!user,
      hasSessionToken: !!sessionToken,
      path: location.pathname,
    });
  }, [isAuthenticated, user, sessionToken, location.pathname]);

  // Check both Redux state and localStorage
  // This handles page refreshes where Redux might not be hydrated yet
  if (!isAuthenticated && !sessionToken) {
    logger.log("Not authenticated, redirecting to login");
    // Store the intended URL for after login (works for both social login and magic link)
    const redirectUrl = location.pathname + location.search;
    logger.log("[ProtectedRoute] Storing redirect URL:", redirectUrl);
    setRedirectUrl(redirectUrl);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If we have a token but not authenticated in Redux yet,
  // show a loading state or let the app verify auth
  if (sessionToken && !isAuthenticated) {
    // You could show a loading spinner here while auth is being verified
    logger.log("Has session token, waiting for auth verification");
  }

  if (requireAdmin && !user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
